(() => {
  const instances = new Set();
  let loadTimer = 0;

  const asObject = (value) => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const clone = (value) => JSON.parse(JSON.stringify(value));
  const collectionSources = ['gift', 'bought_elsewhere', 'swap', 'already_owned', 'other'];

  const normalizeContext = (value) => {
    const source = asObject(value);
    const discovery = asObject(source.discovery);
    const sample = asObject(source.sample);
    const bottle = asObject(source.bottle);
    return {
      version: 1,
      discovery: { decision: ['test_later', 'skip'].includes(discovery.decision) ? discovery.decision : null },
      sample: {
        favorite: sample.favorite === true,
        verdict: ['love', 'like', 'unsure', 'dislike'].includes(sample.verdict) ? sample.verdict : null,
        nextStep: ['wishlist', 'resample'].includes(sample.nextStep) ? sample.nextStep : null,
        impressions: Array.isArray(sample.impressions) ? sample.impressions : [],
        note: typeof sample.note === 'string' ? sample.note.slice(0, 500) : '',
        testedManually: sample.testedManually === true,
        testedAt: typeof sample.testedAt === 'string' ? sample.testedAt.slice(0, 64) : ''
      },
      bottle: {
        favorite: bottle.favorite === true,
        acquisition: ['blind_buy', 'sampled_first'].includes(bottle.acquisition) ? bottle.acquisition : null,
        repurchase: ['yes', 'maybe', 'no'].includes(bottle.repurchase) ? bottle.repurchase : null,
        occasions: Array.isArray(bottle.occasions) ? bottle.occasions : [],
        seasons: Array.isArray(bottle.seasons) ? bottle.seasons : [],
        collectionSource: collectionSources.includes(bottle.collectionSource) ? bottle.collectionSource : null,
        collectionAddedManually: bottle.collectionAddedManually === true,
        collectionAddedAt: typeof bottle.collectionAddedAt === 'string' ? bottle.collectionAddedAt.slice(0, 64) : '',
        removalReason: ['empty', 'sold', 'given_away', 'other'].includes(bottle.removalReason) ? bottle.removalReason : null,
        removedAt: typeof bottle.removedAt === 'string' ? bottle.removedAt.slice(0, 64) : '',
        removalNote: typeof bottle.removalNote === 'string' ? bottle.removalNote.slice(0, 300) : '',
        note: typeof bottle.note === 'string' ? bottle.note.slice(0, 500) : ''
      }
    };
  };

  const post = async (element, action, payload) => {
    const response = await fetch(element.dataset.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        action,
        shopDomain: element.dataset.shopDomain,
        shopifyCustomerGid: element.dataset.customerGid,
        storefrontAccessToken: element.dataset.memberToken,
        ...payload
      })
    });
    const body = await response.json().catch(() => null);
    if (!response.ok || body?.status !== 'ok') throw new Error(body?.message || 'Opslaan is niet gelukt.');
    return body.data || {};
  };

  const sameMemberProduct = (left, right) => left !== right
    && left.isConnected
    && left.dataset.authState === 'member'
    && left.dataset.productGid === right.dataset.productGid
    && left.dataset.customerGid === right.dataset.customerGid;

  const broadcastPreference = (source, preference) => {
    instances.forEach((element) => {
      if (!sameMemberProduct(element, source)) return;
      element.applyPreference(preference, {
        purchasedQuantity: source.purchasedQuantity,
        lastPurchasedAt: source.lastPurchasedAt,
        receivedSampleQuantity: source.receivedSampleQuantity,
        lastSampledAt: source.lastSampledAt
      });
    });
  };

  const closeOtherMobileRails = (current) => {
    instances.forEach((element) => {
      if (element === current) return;
      element.setExpanded(false);
    });
  };

  const loadStates = async () => {
    loadTimer = 0;
    const members = [...instances].filter((element) => element.isConnected && element.dataset.authState === 'member');
    const groups = new Map();
    members.forEach((element) => {
      const key = [element.dataset.endpoint, element.dataset.customerGid, element.dataset.memberToken].join('|');
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(element);
    });

    await Promise.all([...groups.values()].map(async (elements) => {
      const first = elements[0];
      const productGids = [...new Set(elements.map((element) => element.dataset.productGid))].slice(0, 100);
      try {
        const result = await post(first, 'public-member-fragrance-preferences', { productGids });
        const byGid = new Map((Array.isArray(result.products) ? result.products : []).map((product) => [product.productGid, product]));
        elements.forEach((element) => element.applyPreference(byGid.get(element.dataset.productGid) || {}));
      } catch (error) {
        elements.forEach((element) => element.setLoadError(error));
      }
    }));
  };

  const scheduleLoad = () => {
    window.clearTimeout(loadTimer);
    loadTimer = window.setTimeout(loadStates, 45);
  };

  const analyzeCornerBrightness = (image) => new Promise((resolve) => {
    if (!image) return resolve('light');
    const calculate = () => {
      if (!image.naturalWidth || !image.naturalHeight) return resolve('light');
      try {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { willReadFrequently: true });
        canvas.width = 40;
        canvas.height = 40;
        context.drawImage(image, image.naturalWidth * .72, 0, image.naturalWidth * .28, image.naturalHeight * .28, 0, 0, 40, 40);
        const pixels = context.getImageData(0, 0, 40, 40).data;
        let total = 0;
        for (let index = 0; index < pixels.length; index += 4) {
          total += pixels[index + 3] < 128 ? 255 : .299 * pixels[index] + .587 * pixels[index + 1] + .114 * pixels[index + 2];
        }
        resolve(total / (pixels.length / 4) < 160 ? 'dark' : 'light');
      } catch (_error) {
        resolve('light');
      }
    };
    if (image.complete) calculate();
    else image.addEventListener('load', calculate, { once: true });
  });

  class OrnataFragranceActions extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.preference = {};
      this.contextData = normalizeContext(null);
      this.generalNote = '';
      this.ownershipState = null;
      this.purchasedQuantity = 0;
      this.receivedSampleQuantity = 0;
      this.lastPurchasedAt = '';
      this.lastSampledAt = '';
      this.statusTimer = 0;
      this.dataset.expanded = 'false';
      this.dataset.ready = this.dataset.authState === 'member' ? 'false' : 'true';
      this.addEventListener('click', (event) => this.handleClick(event));
      instances.add(this);
      if (this.dataset.authState === 'member') scheduleLoad();
      else this.render();
      if (this.classList.contains('ornata-fragrance-actions--gallery')) this.watchGalleryContrast();
    }

    disconnectedCallback() {
      instances.delete(this);
      this.galleryObserver?.disconnect();
      window.clearTimeout(this.statusTimer);
    }

    applyPreference(preference, overrides = {}) {
      this.preference = preference || {};
      this.contextData = normalizeContext(preference?.contextData);
      this.generalNote = typeof preference?.note === 'string' ? preference.note.slice(0, 500) : '';
      this.ownershipState = ['owned', 'removed'].includes(preference?.ownershipState) ? preference.ownershipState : null;
      this.purchasedQuantity = Math.max(0, Number(overrides.purchasedQuantity ?? preference?.purchasedQuantity) || 0);
      this.receivedSampleQuantity = Math.max(0, Number(overrides.receivedSampleQuantity ?? preference?.receivedSampleQuantity) || 0);
      this.lastPurchasedAt = String(overrides.lastPurchasedAt ?? preference?.lastPurchasedAt ?? '');
      this.lastSampledAt = String(overrides.lastSampledAt ?? preference?.lastSampledAt ?? '');
      this.dataset.ready = 'true';
      this.render();
    }

    setLoadError(error) {
      this.dataset.ready = 'true';
      this.render();
      this.showStatus(error instanceof Error ? error.message : 'Voorkeuren konden niet worden geladen');
    }

    hasSampleHistory() {
      return this.receivedSampleQuantity > 0 || this.contextData.sample.testedManually === true;
    }

    removedBeforeLatestPurchase() {
      if (this.ownershipState !== 'removed') return false;
      const removedAt = Date.parse(this.contextData.bottle.removedAt || '');
      const purchasedAt = Date.parse(this.lastPurchasedAt || '');
      return Number.isFinite(removedAt) && Number.isFinite(purchasedAt) && purchasedAt > removedAt;
    }

    isOwned() {
      if (this.ownershipState === 'removed') return this.removedBeforeLatestPurchase();
      return this.ownershipState === 'owned' || this.purchasedQuantity > 0;
    }

    isWishlist() {
      return !this.isOwned() && this.contextData.sample.nextStep === 'wishlist';
    }

    isTestIntent() {
      if (this.isOwned()) return false;
      return this.hasSampleHistory()
        ? this.contextData.sample.nextStep === 'resample'
        : this.contextData.discovery.decision === 'test_later';
    }

    preferredNote() {
      if (this.generalNote) return this.generalNote;
      if (this.isOwned()) return this.contextData.bottle.note || '';
      if (this.hasSampleHistory()) return this.contextData.sample.note || '';
      return '';
    }

    setExpanded(expanded) {
      this.dataset.expanded = expanded ? 'true' : 'false';
      this.querySelector('[data-fragrance-toggle]')?.setAttribute('aria-expanded', String(Boolean(expanded)));
    }

    setActionLabel(action, label) {
      const button = this.querySelector(`[data-fragrance-action="${action}"]`);
      if (!button) return;
      const text = button.querySelector('[data-fragrance-label]');
      if (text && this.dataset.authState === 'member') text.textContent = label;
      button.setAttribute('aria-label', label);
      button.title = label;
    }

    render() {
      const owned = this.isOwned();
      const sampled = this.hasSampleHistory();
      this.dataset.owned = owned ? 'true' : 'false';
      this.dataset.sampled = sampled ? 'true' : 'false';

      const favorite = this.querySelector('[data-fragrance-action="favorite"]');
      const test = this.querySelector('[data-fragrance-action="test"]');
      const note = this.querySelector('[data-fragrance-action="note"]');
      const remove = this.querySelector('[data-fragrance-action="remove"]');

      favorite?.setAttribute('aria-pressed', String(owned ? this.contextData.bottle.favorite === true : this.isWishlist()));
      test?.setAttribute('aria-pressed', String(this.isTestIntent()));
      if (test) test.hidden = owned;
      if (remove) remove.hidden = !owned;
      if (note) note.hidden = false;

      this.setActionLabel('favorite', owned ? 'Favoriet' : 'Verlanglijst');
      this.setActionLabel('test', sampled ? 'Nog een keer testen' : 'Wil ik testen');
      this.setActionLabel('note', 'Notitie');
      this.setActionLabel('remove', 'Uit collectie verwijderen');
    }

    showStatus(message) {
      const status = this.querySelector('[data-fragrance-status]');
      if (!status) return;
      status.textContent = message;
      window.clearTimeout(this.statusTimer);
      if (message) this.statusTimer = window.setTimeout(() => { status.textContent = ''; }, 2400);
    }

    handleLocked() {
      if (this.dataset.authState === 'guest') {
        const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
        location.href = `/customer_authentication/login?return_to=${returnTo}`;
      } else {
        location.href = '/pages/over-ornata-loyalty';
      }
    }

    closePanels() {
      const notePanel = this.querySelector('[data-fragrance-note-panel]');
      const removalPanel = this.querySelector('[data-fragrance-removal]');
      if (notePanel) notePanel.hidden = true;
      if (removalPanel) removalPanel.hidden = true;
    }

    openNotePanel() {
      this.closePanels();
      const panel = this.querySelector('[data-fragrance-note-panel]');
      const input = this.querySelector('[data-fragrance-note-input]');
      if (!panel || !input) return;
      input.value = this.preferredNote();
      panel.hidden = false;
      window.requestAnimationFrame(() => input.focus({ preventScroll: true }));
    }

    openRemovalPanel() {
      if (!this.isOwned()) return;
      this.closePanels();
      const panel = this.querySelector('[data-fragrance-removal]');
      if (panel) panel.hidden = false;
    }

    async handleClick(event) {
      const toggle = event.target.closest?.('[data-fragrance-toggle]');
      if (toggle) {
        event.preventDefault();
        event.stopPropagation();
        const expanded = this.dataset.expanded !== 'true';
        if (expanded) closeOtherMobileRails(this);
        this.setExpanded(expanded);
        return;
      }

      const cancelPanel = event.target.closest?.('[data-fragrance-panel-cancel]');
      if (cancelPanel) {
        event.preventDefault();
        this.closePanels();
        return;
      }

      const noteSave = event.target.closest?.('[data-fragrance-note-save]');
      if (noteSave) {
        event.preventDefault();
        if (this.dataset.authState !== 'member') return this.handleLocked();
        await this.saveNote(noteSave);
        return;
      }

      const reason = event.target.closest?.('[data-removal-reason]');
      if (reason) {
        event.preventDefault();
        if (this.dataset.authState !== 'member') return this.handleLocked();
        await this.removeFromCollection(reason.dataset.removalReason, reason);
        return;
      }

      const button = event.target.closest?.('[data-fragrance-action]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.dataset.authState !== 'member') return this.handleLocked();
      if (this.dataset.ready !== 'true') return this.showStatus('Voorkeuren worden geladen…');

      const action = button.dataset.fragranceAction;
      if (action === 'note') return this.openNotePanel();
      if (action === 'remove') return this.openRemovalPanel();
      await this.saveAction(action, button);
    }

    normalizeRecoveredOwnership(context) {
      if (this.ownershipState !== 'removed' || !this.removedBeforeLatestPurchase()) return;
      this.ownershipState = 'owned';
      context.bottle.removalReason = null;
      context.bottle.removedAt = '';
      context.bottle.removalNote = '';
    }

    legacyFields(context) {
      let sentiment = null;
      if (context.bottle.favorite || context.sample.favorite) sentiment = 'favorite';
      else if (context.sample.verdict === 'dislike' || context.discovery.decision === 'skip') sentiment = 'no_interest';
      else if (context.sample.verdict === 'unsure') sentiment = 'unsure';
      else if (['love', 'like'].includes(context.sample.verdict)) sentiment = 'like';

      let intent = null;
      if (sentiment !== 'no_interest') {
        if (context.sample.nextStep === 'wishlist') intent = 'wishlist';
        else if (context.sample.nextStep === 'resample' || context.discovery.decision === 'test_later') intent = 'sample';
      }
      return { sentiment, intent };
    }

    async saveAction(action, button) {
      const previous = this.snapshot();
      const context = clone(this.contextData);
      this.normalizeRecoveredOwnership(context);

      if (action === 'favorite') {
        if (this.isOwned()) {
          context.bottle.favorite = context.bottle.favorite !== true;
        } else {
          const active = context.sample.nextStep === 'wishlist';
          context.sample.nextStep = active ? null : 'wishlist';
          if (!active && context.sample.verdict === 'dislike') context.sample.verdict = null;
        }
      } else if (action === 'test') {
        if (this.isOwned()) return;
        if (this.hasSampleHistory()) {
          const active = context.sample.nextStep === 'resample';
          context.sample.nextStep = active ? null : 'resample';
          if (!active && context.sample.verdict === 'dislike') context.sample.verdict = null;
        } else {
          context.discovery.decision = context.discovery.decision === 'test_later' ? null : 'test_later';
        }
      } else {
        return;
      }

      await this.persist(context, this.generalNote, button, previous);
    }

    async saveNote(button) {
      const input = this.querySelector('[data-fragrance-note-input]');
      if (!input) return;
      const note = String(input.value || '').trim().slice(0, 500);
      const previous = this.snapshot();
      const context = clone(this.contextData);
      this.normalizeRecoveredOwnership(context);
      if (this.isOwned()) context.bottle.note = note;
      else if (this.hasSampleHistory()) context.sample.note = note;
      const saved = await this.persist(context, note, button, previous);
      if (saved) this.closePanels();
    }

    async removeFromCollection(reason, button) {
      if (!this.isOwned() || !['empty', 'sold', 'given_away', 'other'].includes(reason)) return;
      const previous = this.snapshot();
      const context = clone(this.contextData);
      context.bottle.removalReason = reason;
      context.bottle.removedAt = new Date().toISOString();
      context.bottle.removalNote = '';
      this.ownershipState = 'removed';
      const saved = await this.persist(context, this.generalNote, button, previous);
      if (saved) this.closePanels();
    }

    snapshot() {
      return {
        contextData: clone(this.contextData),
        generalNote: this.generalNote,
        ownershipState: this.ownershipState
      };
    }

    restore(snapshot) {
      this.contextData = snapshot.contextData;
      this.generalNote = snapshot.generalNote;
      this.ownershipState = snapshot.ownershipState;
      this.render();
    }

    async persist(context, note, button, previous) {
      button.disabled = true;
      this.contextData = context;
      this.generalNote = note;
      this.render();
      this.showStatus('Opslaan…');
      try {
        const legacy = this.legacyFields(context);
        const result = await post(this, 'public-update-fragrance-preference', {
          fragrancePreference: {
            productGid: this.dataset.productGid,
            ownershipState: this.ownershipState,
            sentiment: legacy.sentiment,
            intent: legacy.intent,
            note,
            contextData: context
          }
        });
        const savedPreference = result.preference || {};
        this.preference = savedPreference;
        this.ownershipState = ['owned', 'removed'].includes(savedPreference.ownershipState)
          ? savedPreference.ownershipState
          : this.ownershipState;
        this.contextData = normalizeContext(savedPreference.contextData || context);
        this.generalNote = typeof savedPreference.note === 'string' ? savedPreference.note : note;
        this.dataset.ready = 'true';
        this.render();
        broadcastPreference(this, {
          ...savedPreference,
          ownershipState: this.ownershipState,
          contextData: this.contextData,
          note: this.generalNote
        });
        this.showStatus('Opgeslagen');
        return true;
      } catch (error) {
        this.restore(previous);
        this.showStatus(error instanceof Error ? error.message : 'Opslaan is niet gelukt');
        return false;
      } finally {
        button.disabled = false;
      }
    }

    watchGalleryContrast() {
      const gallery = this.closest('.product-gallery__image-list');
      const carousel = gallery?.querySelector('.product-gallery__carousel');
      if (!gallery || !carousel) return;
      let timer = 0;
      const update = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(async () => {
          const center = carousel.getBoundingClientRect().left + carousel.clientWidth / 2;
          const media = [...carousel.querySelectorAll('.product-gallery__media:not([hidden])')]
            .sort((left, right) => Math.abs((left.getBoundingClientRect().left + left.clientWidth / 2) - center) - Math.abs((right.getBoundingClientRect().left + right.clientWidth / 2) - center))[0];
          const image = media?.querySelector('img');
          this.dataset.imageBright = await analyzeCornerBrightness(image);
        }, 45);
      };
      carousel.addEventListener('scroll', update, { passive: true });
      this.galleryObserver = new MutationObserver(update);
      this.galleryObserver.observe(carousel, { subtree: true, attributes: true, attributeFilter: ['hidden', 'class', 'src'] });
      update();
    }
  }

  if (!customElements.get('ornata-fragrance-actions')) {
    customElements.define('ornata-fragrance-actions', OrnataFragranceActions);
  }
})();
